import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import SafeImg from "@/components/common/SafeImg";
import VideoModal from "@/components/resources/VideoModal";
import Reveal from "./Reveal";
import { BTN_PRIMARY, HOME_WRAP } from "./homeTheme";

/**
 * "Precision Manufacturing" — an immersive editorial layout, not a 50/50 card: a
 * large photograph bleeds off the left edge and fades into the navy section, the
 * copy sits over its right side, and a small inset image overlaps the corner.
 * Content is `data.factory`: title, description, checklist, button, images and the
 * optional facility video (opens the same modal used by Resources → Videos).
 */
export default function FacilitySection({ data }) {
  const f = data?.factory || {};
  const [playing, setPlaying] = useState(false);
  const main = f.primaryImage || f.galleryImage || f.secondaryImage;
  const inset = f.tertiaryImage || f.galleryImage;
  const checklist = (Array.isArray(f.checklist) ? f.checklist : []).filter(Boolean).slice(0, 4);
  if (!f.title && !main) return null;
  const titleLines = String(f.title || "").trim().split(/(?<=[.!?])\s+/).filter(Boolean);

  return (
    <section className="relative isolate overflow-hidden bg-[#071B3A] text-white" aria-labelledby="home-facility-title">
      {/* Photograph */}
      <div className="relative aspect-[16/10] w-full sm:aspect-[16/9] lg:absolute lg:inset-y-0 lg:left-0 lg:aspect-auto lg:w-[66%]">
        {main && (
          <SafeImg
            src={toAbsoluteUrl(main)}
            alt="Technician working on Shingare industrial equipment"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-[65%_center]"
          />
        )}
        <div className="absolute inset-0 bg-[#071B3A]/25" aria-hidden="true" />
        <div className="absolute inset-y-0 right-0 hidden w-2/3 bg-gradient-to-r from-transparent via-[#071B3A]/70 to-[#071B3A] lg:block" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#071B3A] to-transparent lg:hidden" aria-hidden="true" />
        {f.videoUrl && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label="Play facility video"
            data-testid="factory-play-video"
            className="group absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white backdrop-blur-md transition-all duration-300 hover:scale-105 hover:bg-[#1769FF] hover:border-[#1769FF] lg:left-[38%]"
          >
            <Play className="ml-1 h-7 w-7" fill="currentColor" aria-hidden="true" />
          </button>
        )}
        {inset && (
          <div className="absolute bottom-6 left-5 hidden aspect-[4/3] w-44 overflow-hidden rounded-2xl border border-white/20 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.8)] sm:block lg:bottom-10 lg:left-10 lg:w-56">
            <img src={toAbsoluteUrl(inset)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
        )}
      </div>

      {/* Copy */}
      <div className={`${HOME_WRAP} relative py-16 sm:py-20 lg:pointer-events-none lg:flex lg:min-h-[720px] lg:items-center lg:py-28`}>
        <Reveal className="lg:pointer-events-auto lg:ml-auto lg:w-[44%]">
          <div className="mb-5 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#7FB0FF]">
            <span className="h-px w-8 bg-[#7FB0FF]" aria-hidden="true" />
            {f.eyebrow || "Precision Manufacturing"}
          </div>
          <h2 id="home-facility-title" className="font-heading text-[34px] font-semibold leading-[1.1] tracking-[-0.02em] sm:text-[42px] lg:text-[48px]">
            {titleLines.map((l) => <span key={l} className="block">{l}</span>)}
          </h2>
          {f.description && <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70 lg:text-lg">{f.description}</p>}
          {checklist.length > 0 && (
            <ul className="mt-10 grid grid-cols-1 gap-x-8 gap-y-0 sm:grid-cols-2">
              {checklist.map((c, i) => (
                <li key={c} className="flex items-baseline gap-4 border-t border-white/15 py-4">
                  <span className="font-heading text-xs tabular-nums text-[#7FB0FF]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[15px] font-medium text-white">{c}</span>
                </li>
              ))}
            </ul>
          )}
          {f.button?.label && (
            <Link to={f.button.link || "/company/manufacturing-facility"} className={`${BTN_PRIMARY} mt-10`}>
              {f.button.label}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          )}
        </Reveal>
      </div>
      <VideoModal video={playing ? { title: f.title || "Facility Tour", external_url: f.videoUrl, file_url: f.videoUrl } : null} onClose={() => setPlaying(false)} />
    </section>
  );
}
